// client/src/coms/conversations/useConversationIndex.js
// One list of conversations from everywhere, for Chats (phones) and the quick switcher: pinned conversation
// tabs, channels and DMs with unread messages or mentions, and recently visited conversations. Built only from
// what already exists (servers and their channels, GET /dms, per-server channel read states, the server unread
// summary, conversation tabs and the visit history); nothing is copied into a store of its own.
// Polls only while `enabled` (the view or the switcher is open).
import { useEffect, useMemo, useState } from 'react';
import { comsApiGet } from '../comsApi';
import { readRecents } from './recentConversations';
import { previewText } from './previewText';

const POLL_MS = 10000;
const MAX_READ_STATE_SERVERS = 8;

export function serverKeyOf(s) {
  if (!s || typeof s !== 'object') return '';
  return String(s.id != null ? s.id : s.public_id != null ? s.public_id : s.server_key != null ? s.server_key : '').trim();
}

export function channelDbId(channelId) {
  const m = /^(?:text|voice):(\d+)$/.exec(String(channelId || ''));
  return m ? Number(m[1]) : 0;
}

export function avatarUrlFor(userId) {
  return userId ? `/uploads/voiceservers/users/${encodeURIComponent(userId)}/avatar.webp` : '';
}

const timeOf = (v) => {
  const t = Date.parse(v || '');
  return Number.isFinite(t) ? t : 0;
};

function norm(s) {
  return String(s || '').toLowerCase();
}

// Pure: builds the sections. Exported for tests.
export function buildConversationIndex({ servers = [], threads = [], readStates = {}, recents = [], tabs = [] }) {
  const serverByKey = new Map(servers.map((s) => [serverKeyOf(s), s]));
  const byId = new Map();

  const channelItem = (sk, cid) => {
    const s = serverByKey.get(sk);
    if (!s) return null;
    const ch = (Array.isArray(s.channels) ? s.channels : []).find((c) => String(c.id) === cid);
    if (!ch || String(ch.kind || 'text') === 'voice' || /^voice:/.test(cid)) return null;
    const st = (readStates[sk] || {})[channelDbId(cid)] || {};
    return {
      id: `c:${sk}:${cid}`,
      kind: 'channel',
      title: String(ch.name || 'channel'),
      subtitle: String(s.name || ''),
      serverKey: sk,
      serverName: String(s.name || ''),
      serverLogo: String(s.logo_url || s.logoUrl || ''),
      unread: Number(st.unread_count) > 0 && Number(st.show_notifications) !== 0,
      mentions: Math.max(0, Number(st.mention_count) || 0),
      preview: '',
      at: 0,
      open: { kind: 'channel', server_key: sk, channel_kind: 'text', channel_id: channelDbId(cid) },
    };
  };
  const dmItem = (th) => {
    if (!th) return null;
    const other = th.other_user || {};
    const uid = Number(th.other_user_id || other.id) || 0;
    return {
      id: `d:${th.id}`,
      kind: 'dm',
      title: String(other.username || th.username || 'Direct message'),
      subtitle: 'Direct message',
      otherUserId: uid,
      avatarUrl: avatarUrlFor(uid),
      unread: !!th.has_unread,
      mentions: 0,
      preview: previewText(th.latest_message_preview),
      at: timeOf(th.latest_message_created_at || th.last_message_at || th.updated_at),
      open: { kind: 'dm', thread_id: Number(th.id) },
    };
  };
  const resolve = (id) => {
    if (byId.has(id)) return byId.get(id);
    let item = null;
    const m = /^c:([^:]+):((?:text|voice):\d+)$/.exec(id);
    if (m) item = channelItem(m[1], m[2]);
    const d = /^d:(\d+)$/.exec(id);
    if (d) item = dmItem(threads.find((t) => String(t.id) === d[1]));
    if (item) byId.set(id, item);
    return item;
  };

  // DMs: every thread is a conversation.
  for (const th of threads) resolve(`d:${th.id}`);
  // Channels with unread messages or mentions on any server we have read states for.
  for (const [sk, states] of Object.entries(readStates)) {
    const s = serverByKey.get(sk);
    for (const c of (s && Array.isArray(s.channels) ? s.channels : [])) {
      const st = states[channelDbId(c.id)];
      if (st && (Number(st.unread_count) > 0 || Number(st.mention_count) > 0)) resolve(`c:${sk}:${c.id}`);
    }
  }
  for (const r of recents) {
    const it = resolve(r.id);
    if (it) it.at = Math.max(it.at, r.at);
  }
  const pinnedIds = tabs.filter((t) => t.pinned).map((t) => t.id);
  for (const id of pinnedIds) resolve(id);

  const used = new Set();
  const take = (list) => list.filter((it) => it && !used.has(it.id) && used.add(it.id));
  const pinned = take(pinnedIds.map((id) => byId.get(id)));
  const all = Array.from(byId.values());
  const unread = take(all.filter((it) => it.unread || it.mentions > 0).sort((a, b) => b.mentions - a.mentions || b.at - a.at));
  const recent = take(all.filter((it) => it.at > 0).sort((a, b) => b.at - a.at));
  return { pinned, unread, recent, all };
}

// Search over every text channel of every server and every DM, not only recent ones.
export function searchConversations({ servers = [], threads = [], readStates = {} }, query) {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const { all } = buildConversationIndex({ servers, threads, readStates });
  const seen = new Set(all.map((it) => it.id));
  const extra = [];
  for (const s of servers) {
    const sk = serverKeyOf(s);
    for (const c of Array.isArray(s.channels) ? s.channels : []) {
      const id = `c:${sk}:${c.id}`;
      if (seen.has(id)) continue;
      const one = buildConversationIndex({ servers: [s], readStates, recents: [{ id, at: 1 }] }).all[0];
      if (one) {
        one.at = 0;
        extra.push(one);
      }
    }
  }
  const hay = (it) => norm(`${it.title} ${it.serverName || ''} ${it.subtitle}`);
  return [...all, ...extra]
    .filter((it) => words.every((w) => hay(it).includes(w)))
    .sort((a, b) => (norm(b.title).startsWith(words[0]) ? 1 : 0) - (norm(a.title).startsWith(words[0]) ? 1 : 0) || b.at - a.at)
    .slice(0, 40);
}

export default function useConversationIndex({ enabled, userId, servers, workspaceItems = [], serverUnreadById = {} }) {
  const [threads, setThreads] = useState([]);
  const [readStates, setReadStates] = useState({});
  const [recents, setRecents] = useState(() => readRecents(userId));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const on = () => setRecents(readRecents(userId));
    on();
    window.addEventListener('vox-recents-changed', on);
    return () => window.removeEventListener('vox-recents-changed', on);
  }, [userId]);

  // Servers whose read states matter: those with unread messages, plus those of recent and pinned channels.
  const serverKeys = useMemo(() => {
    const keys = new Set();
    for (const [k, v] of Object.entries(serverUnreadById || {})) if (v) keys.add(k);
    for (const r of recents) {
      const m = /^c:([^:]+):/.exec(r.id);
      if (m) keys.add(m[1]);
    }
    for (const it of workspaceItems) if (it.kind === 'channel' && it.tab) keys.add(it.tab.s);
    return Array.from(keys).sort().slice(0, MAX_READ_STATE_SERVERS).join(',');
  }, [serverUnreadById, recents, workspaceItems]);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let alive = true;
    let timer = null;
    const run = async () => {
      try {
        const res = await comsApiGet(`/dms?_=${Date.now()}`);
        if (alive) setThreads(Array.isArray(res && res.threads) ? res.threads : []);
      } catch {}
      const next = {};
      for (const key of serverKeys ? serverKeys.split(',') : []) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await comsApiGet(`/servers/${encodeURIComponent(key)}/channel-read-states`);
          next[key] = (res && res.states) || {};
        } catch {}
      }
      if (!alive) return;
      setReadStates((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      setLoaded(true);
      timer = setTimeout(run, POLL_MS);
    };
    run();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, userId, serverKeys]);

  const tabs = useMemo(() => workspaceItems.map((it) => ({ id: it.id, pinned: !!it.pinned })), [workspaceItems]);
  const index = useMemo(() => buildConversationIndex({ servers: servers || [], threads, readStates, recents, tabs }), [servers, threads, readStates, recents, tabs]);

  return { ...index, loaded, search: (q) => searchConversations({ servers: servers || [], threads, readStates }, q) };
}
